/**
 * AW Popover
 * Shared open/close + positioning controller using Floating UI.
 *
 * Trigger:  [data-aw-popover-trigger="<panel-id>"]
 * Content:  id="<panel-id>"  [data-aw-popover-content]
 * Floating: [data-aw-popover-floating] on content → Floating UI positions it
 *           [data-aw-popover-placement] on content → placement (default: bottom-end)
 *           [data-aw-popover-strategy] on content → "absolute" (default) or "fixed"
 *
 * Exposed:  window.AwPopover.open(trigger, panel) / .close(trigger, panel) / .closeAll()
 */

(function () {
  const { computePosition, flip, shift, offset } = FloatingUIDOM;

  function getPanel(trigger) {
    const id = trigger.dataset.awPopoverTrigger;
    return id ? document.getElementById(id) : null;
  }

  function isOpen(trigger) {
    return trigger.getAttribute('aria-expanded') === 'true';
  }

  async function openPopover(trigger, panel) {
    // Mark open first so CSS makes it visible (needed for FloatingUI to measure)
    trigger.setAttribute('aria-expanded', 'true');
    trigger.classList.add('aw-popover--open');
    panel.removeAttribute('hidden');
    panel.classList.add('aw-popover--open');

    // Position after it's visible
    if (panel.hasAttribute('data-aw-popover-floating')) {
      const strategy = panel.dataset.awPopoverStrategy || 'absolute';
      const placement = panel.dataset.awPopoverPlacement || 'bottom-end';

      const { x, y } = await computePosition(trigger, panel, {
        strategy,
        placement,
        middleware: [offset(8), flip({ padding: 12 }), shift({ padding: 12 })],
      });

      Object.assign(panel.style, { position: strategy, left: `${x}px`, top: `${y}px` });
    }

    const focusTarget = panel.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusTarget) setTimeout(() => focusTarget.focus(), 50);
  }

  function closePopover(trigger, panel) {
    trigger.setAttribute('aria-expanded', 'false');
    trigger.classList.remove('aw-popover--open');
    panel.classList.remove('aw-popover--open');
  }

  function closeAll() {
    document.querySelectorAll('[data-aw-popover-trigger]').forEach((t) => {
      const p = getPanel(t);
      if (p && isOpen(t)) closePopover(t, p);
    });
  }

  function init(root = document) {
    root.querySelectorAll('[data-aw-popover-trigger]').forEach((trigger) => {
      if (trigger.dataset.awPopoverInit) return;
      trigger.dataset.awPopoverInit = '1';

      trigger.addEventListener('click', (e) => {
        e.stopPropagation(); // prevent document closeAll from firing on this click
        const panel = getPanel(trigger);
        if (!panel) return;
        if (isOpen(trigger)) {
          closePopover(trigger, panel);
        } else {
          closeAll(); // close any other open popovers first
          openPopover(trigger, panel);
        }
      });
    });
  }

  // Close when clicking outside any open content or trigger
  document.addEventListener('click', (e) => {
    if (
      e.target.closest('[data-aw-popover-content]') ||
      e.target.closest('[data-aw-popover-trigger]')
    ) return;
    closeAll();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAll();
  });

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  window.AwPopover = { open: openPopover, close: closePopover, closeAll };
})();